import { useState, useEffect } from 'react'
import { REGIONS } from './regions'

export default function RegionPickerModal({ isOpen, onClose, onApply, initialRegion, initialDistrict, initialDistance, userAddress }) {
    const [selectedRegion, setSelectedRegion] = useState(initialRegion || '')
    const [selectedDistrict, setSelectedDistrict] = useState(initialDistrict || '')
    const [activeTab, setActiveTab] = useState('region') // 'region' | 'distance'
    const [distance, setDistance] = useState(initialDistance || 25) // km (5 ~ 50, or 'all')

    useEffect(() => {
        if (isOpen) {
            setSelectedRegion(initialRegion || '')
            setSelectedDistrict(initialDistrict || '')
            setDistance(initialDistance || 25)
            // If initialDistance is set, switch to distance tab
            if (initialDistance && initialDistance !== 50) {
                setActiveTab('distance')
            }
        }
    }, [isOpen, initialRegion, initialDistrict, initialDistance])

    if (!isOpen) return null

    // Keys for Left Column (exclude empty '' key if present for logic)
    const regionKeys = Object.keys(REGIONS).filter(r => r !== '')

    // Districts for Right Column
    const districts = selectedRegion ? REGIONS[selectedRegion] : []

    const handleReset = () => {
        setSelectedRegion('')
        setSelectedDistrict('')
        setDistance(25)
    }

    const handleApply = () => {
        if (activeTab === 'distance') {
            onApply(null, null, distance)
        } else {
            onApply(selectedRegion, selectedDistrict, null)
        }
        onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
            <div className="bg-white w-full max-w-lg sm:rounded-xl rounded-t-xl h-[80vh] sm:h-[600px] flex flex-col shadow-2xl animate-slideUp sm:animate-none">

                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-bold text-gray-800">희망 근무 위치</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 p-1">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex p-2 gap-2 bg-white">
                    <button
                        onClick={() => setActiveTab('region')}
                        className={`flex-1 py-3 text-sm font-bold rounded-lg border transition ${activeTab === 'region'
                            ? 'bg-white border-blue-500 text-blue-600 ring-1 ring-blue-500'
                            : 'bg-gray-100 border-transparent text-gray-500'
                            }`}
                    >
                        지역으로 선택
                    </button>
                    <button
                        onClick={() => setActiveTab('distance')}
                        className={`flex-1 py-3 text-sm font-bold rounded-lg border transition ${activeTab === 'distance'
                            ? 'bg-white border-blue-500 text-blue-600 ring-1 ring-blue-500'
                            : 'bg-gray-100 border-transparent text-gray-500'
                            }`}
                    >
                        거리로 선택
                    </button>
                </div>

                {/* Content Area (2 Columns or Distance UI) */}
                {activeTab === 'region' ? (
                    <div className="flex flex-1 overflow-hidden border-t border-b">
                        {/* Left: Regions */}
                        <div className="w-1/3 border-r overflow-y-auto bg-white">
                            <button
                                onClick={() => {
                                    setSelectedRegion('')
                                    setSelectedDistrict('')
                                }}
                                className={`w-full text-left px-4 py-3 text-sm font-medium border-b border-gray-50 ${selectedRegion === '' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                전체
                            </button>
                            {regionKeys.map(r => (
                                <button
                                    key={r}
                                    onClick={() => {
                                        setSelectedRegion(r)
                                        setSelectedDistrict('')
                                    }}
                                    className={`w-full text-left px-4 py-3 text-sm font-medium border-b border-gray-50 ${selectedRegion === r ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    {r}
                                </button>
                            ))}
                        </div>

                        {/* Right: Districts */}
                        <div className="flex-1 overflow-y-auto bg-gray-50/30">
                            {selectedRegion ? (
                                <>
                                    {/* 'All' option for the specific region */}
                                    <button
                                        onClick={() => setSelectedDistrict('전체 (구)')}
                                        className={`w-full text-left px-5 py-3 text-sm flex justify-between items-center ${selectedDistrict === '전체 (구)' || selectedDistrict === ''
                                            ? 'text-blue-600 font-bold bg-blue-50'
                                            : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                    >
                                        <span>전체</span>
                                        {(selectedDistrict === '전체 (구)' || selectedDistrict === '') && (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        )}
                                    </button>

                                    {/* Specific Districts */}
                                    {districts.filter(d => d !== '전체 (구)').map(d => (
                                        <button
                                            key={d}
                                            onClick={() => setSelectedDistrict(d)}
                                            className={`w-full text-left px-5 py-3 text-sm flex justify-between items-center ${selectedDistrict === d ? 'text-blue-600 font-bold bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                        >
                                            <span>{d}</span>
                                            {selectedDistrict === d && (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                            )}
                                        </button>
                                    ))}
                                </>
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                    지역을 선택해주세요.
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center p-6 bg-white animate-fadeIn">

                        {/* Address Display */}
                        <div className="mb-10 text-center">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-blue-500 mb-3">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                            </div>
                            <p className="text-gray-500 text-sm mb-1">현재 설정된 주소</p>
                            <p className="text-lg font-bold text-gray-900 px-4">
                                {userAddress || '주소 정보가 없습니다'}
                            </p>
                            {!userAddress && <p className="text-xs text-red-400 mt-1">마이페이지에서 주소를 설정해주세요.</p>}
                        </div>

                        {/* Slider UI */}
                        <div className="w-full max-w-sm bg-gray-50 rounded-2xl p-6 border border-gray-100">
                            <div className="text-center mb-6">
                                <span className="text-lg font-bold text-gray-900">
                                    집 주변 <span className="text-blue-600">{distance >= 50 ? '전체' : `${distance}km`}</span> 이내
                                </span>
                            </div>

                            <div className="relative h-10 flex items-center mb-2">
                                <input
                                    type="range"
                                    min="5"
                                    max="50"
                                    step="5"
                                    value={distance}
                                    onChange={(e) => setDistance(parseInt(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                />
                            </div>
                            <div className="flex justify-between text-xs text-gray-400 font-medium px-1">
                                <span>5km</span>
                                <span>전체</span>
                            </div>
                        </div>

                    </div>
                )}

                {/* Footer */}
                <div className="p-4 flex gap-3 border-t bg-white">
                    <button
                        onClick={handleReset}
                        className="flex-none px-4 py-3 text-gray-600 font-medium whitespace-nowrap"
                    >
                        초기화
                    </button>
                    <button
                        onClick={handleApply}
                        className="flex-1 bg-blue-500 text-white rounded-lg font-bold py-3 hover:bg-blue-600 transition shadow-lg shadow-blue-200"
                    >
                        적용
                    </button>
                </div>
            </div>
        </div>
    )
}
